import { create } from 'zustand';
import { http } from '../api/http';

export interface SpectatorBet {
  id: number;
  matchId: string;
  wallet: string;
  predictedWinner: string;
  amountTokens: string;
  status: 'pending' | 'won' | 'lost' | 'cancelled' | 'refunded';
  payoutTokens: string | null;
  seasonId: number;
  placedAt: string;
  resolvedAt: string | null;
}

export interface MatchOdds {
  player: string;
  totalBet: string;
  betCount: number;
}

interface BettingState {
  activeBets: SpectatorBet[];
  betHistory: SpectatorBet[];
  matchOdds: MatchOdds[];
  loading: boolean;

  fetchActiveBets: (wallet: string) => Promise<void>;
  fetchBetHistory: (wallet: string) => Promise<void>;
  fetchMatchOdds: (matchId: string) => Promise<void>;
  placeBet: (
    wallet: string,
    matchId: string,
    predictedWinner: string,
    amountTokens: string,
  ) => Promise<SpectatorBet | null>;
}

export const useBettingStore = create<BettingState>((set) => ({
  activeBets: [],
  betHistory: [],
  matchOdds: [],
  loading: false,

  fetchActiveBets: async (wallet: string) => {
    try {
      const res = await http.get<{ bets: SpectatorBet[] }>(`/bets/${wallet}/active`);
      set({ activeBets: res.bets });
    } catch (error) {
      console.error('Failed to fetch active bets:', error);
    }
  },

  fetchBetHistory: async (wallet: string) => {
    try {
      const res = await http.get<{ bets: SpectatorBet[] }>(`/bets/${wallet}/history`);
      set({ betHistory: res.bets });
    } catch (error) {
      console.error('Failed to fetch bet history:', error);
    }
  },

  fetchMatchOdds: async (matchId: string) => {
    try {
      const res = await http.get<{ odds: MatchOdds[] }>(`/matches/${matchId}/odds`);
      set({ matchOdds: res.odds });
    } catch (error) {
      console.error('Failed to fetch match odds:', error);
    }
  },

  placeBet: async (
    wallet: string,
    matchId: string,
    predictedWinner: string,
    amountTokens: string,
  ) => {
    set({ loading: true });
    try {
      const res = await http.post<{ bet: SpectatorBet }>('/bets/place', {
        wallet,
        matchId,
        predictedWinner,
        amountTokens,
      });
      set((state) => ({
        activeBets: [res.bet, ...state.activeBets],
        loading: false,
      }));
      return res.bet;
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },
}));
